import React, { useState, useEffect } from 'react';
import { useToast } from '../contexts/ToastContext';
import { getApplications, getClients, createClientApplication } from '../services/apiService';
import { useNavigate } from 'react-router-dom';

function ApplicationsPage() {
  const { showToast } = useToast();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedAppId, setSelectedAppId] = useState(null);
  
  // Form state
  const [clientId, setClientId] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [clients, setClients] = useState([]);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [selectedClientName, setSelectedClientName] = useState('');
  const [checkingSubdomain, setCheckingSubdomain] = useState(false);
  const [subdomainStatus, setSubdomainStatus] = useState(null);
  const [superadminEmail, setSuperadminEmail] = useState('');
  const [clientsLoading, setClientsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const navigate = useNavigate();
  
  useEffect(() => {
    loadApplications();
  }, []);
  
  const loadApplications = async () => {
    try {
      setLoading(true);
      const response = await getApplications();
      
      if (response && response.success && Array.isArray(response.result)) {
        setApplications(response.result);
        setError(null);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Failed to load applications:', error);
      setError(error.message || 'Failed to load applications');
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };
  
  const loadClients = async () => {
    try {
      setClientsLoading(true);
      const response = await getClients();
      
      if (response && response.success && Array.isArray(response.result)) {
        setClients(response.result);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Failed to load clients:', error);
      showToast('Failed to load clients: ' + error.message, 'error');
    } finally {
      setClientsLoading(false);
    }
  };
  
  const openDemoBuilder = (appId = null, e) => {
    // Prevent event bubbling if event exists
    if (e) e.stopPropagation();
    
    setSelectedAppId(appId);
    setClientId('');
    setSubdomain('');
    setSelectedClientName('');
    loadClients(); // Load clients when modal opens
    setShowModal(true);
  };
  
  const closeModal = () => {
    setShowModal(false);
    // Reset form state
    setClientId('');
    setSubdomain('');
    setSuperadminEmail('');
    setSelectedClientName('');
    setClientSearchTerm('');
  };
  
  const handleClientSelect = (client) => {
    setClientId(client.id);
    setSelectedClientName(client.name);
    setShowClientDropdown(false);
  };
  
  const checkSubdomainAvailability = async () => {
    if (subdomain.length < 4) {
      setSubdomainStatus({
        available: false,
        message: 'Subdomain too short (min 4 characters)'
      });
      return false;
    }
    
    setCheckingSubdomain(true);
    
    // Simulate API call with timeout
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setCheckingSubdomain(false);
    setSubdomainStatus({
      available: true,
      message: 'Subdomain is available!'
    });
    
    return true;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!clientId) {
      showToast('Please select a client', 'error');
      return;
    }
    
    if (!subdomain) {
      showToast('Please enter a subdomain', 'error');
      return;
    }

    if (!superadminEmail) {
      showToast('Please enter superadmin email', 'error');
      return;
    }
    
    try {
      setSubmitting(true);
      
      const response = await createClientApplication({
        clientId: parseInt(clientId),
        applicationId: parseInt(selectedAppId),
        subdomain: subdomain.trim().toLowerCase(),
        adminEmail: superadminEmail
      });

      if (response.success) {
        showToast('Demo account created successfully!', 'success');
        setShowModal(false);
        navigate('/deployments'); // Navigate to deployments page
      } else {
        throw new Error(response.message || 'Failed to create demo account');
      }
    } catch (error) {
      console.error('Error creating demo account:', error);
      showToast(error.message, 'error');
    } finally {
      setSubmitting(false);
    }
  };
  
  const filteredClients = clients.filter(client => 
    client.name.toLowerCase().includes(clientSearchTerm.toLowerCase())
  );
  
  if (loading) {
    return (
      <div className="loading-spinner">
        <i className="fas fa-spinner fa-spin"></i> Loading applications...
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="error-state">
        <i className="fas fa-exclamation-circle"></i>
        <p>{error}</p>
        <button className="btn btn-primary" onClick={loadApplications}>
          Try Again
        </button>
      </div>
    );
  }
  
  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Applications</h1>
        <p className="page-description">Manage your applications</p>
      </div>
      
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">All Applications</h2>
          <button className="btn btn-primary" onClick={() => openDemoBuilder()}>
            <i className="fas fa-plus"></i> Add New Application
          </button>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="loading-spinner">
              <i className="fas fa-spinner fa-spin"></i> Loading applications...
            </div>
          ) : error ? (
            <div className="error-state">
              <i className="fas fa-exclamation-circle"></i>
              <p>{error}</p>
              <button className="btn btn-primary" onClick={loadApplications}>
                Try Again
              </button>
            </div>
          ) : applications.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-folder-open"></i>
              <p>No applications found</p>
              <p className="empty-state-description">Create your first application to get started</p>
              <button className="btn btn-primary" onClick={() => openDemoBuilder()}>
                Create Application
              </button>
            </div>
          ) : (
            <div className="applications-grid">
              {applications.map(app => (
                <div key={app.id} className="application-card">
                  <div className="application-header">
                    <span>{app.name}</span>
                    <span className="status-badge status-active">Active</span>
                  </div>
                  <div className="application-body">
                    <div className="application-info">
                      <div className="info-item">
                        <span className="info-label">Domain:</span>
                        <span className="info-value">{app.domain}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Created:</span>
                        <span className="info-value">
                          {new Date(app.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="application-actions">
                      <button 
                        className="btn btn-primary btn-sm" 
                        onClick={(e) => openDemoBuilder(app.id, e)}
                      >
                        <i className="fas fa-rocket"></i> Demo Account Builder
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Demo Account Builder Modal */}
      {showModal && (
        <div className="modal-backdrop">
          <div className="modal">
            <div className="modal-content">
              <div className="modal-header">
                <h2>Create Demo Account</h2>
                <button 
                  type="button"
                  className="modal-close" 
                  onClick={closeModal}
                  disabled={submitting}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  {/* Client Selection */}
                  <div className="form-group">
                    <label htmlFor="clientSelect">Client</label>
                    <div className="select-container">
                      <div 
                        className="select-display"
                        onClick={() => setShowClientDropdown(!showClientDropdown)}
                      >
                        {clientsLoading ? (
                          <span className="loading-text">
                            <i className="fas fa-spinner fa-spin"></i> Loading clients...
                          </span>
                        ) : (
                          <>
                            <span>{selectedClientName || 'Select a client...'}</span>
                            <i className={`fas fa-chevron-${showClientDropdown ? 'up' : 'down'}`}></i>
                          </>
                        )}
                      </div>
                      {showClientDropdown && !clientsLoading && (
                        <div className="select-dropdown">
                          <div className="select-search-container">
                            <i className="fas fa-search"></i>
                            <input
                              type="text"
                              placeholder="Search clients..."
                              value={clientSearchTerm}
                              onChange={(e) => setClientSearchTerm(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          <div className="select-options">
                            {filteredClients.length === 0 ? (
                              <div className="select-option no-results">
                                No clients found
                              </div>
                            ) : (
                              filteredClients.map(client => (
                                <div
                                  key={client.id}
                                  className={`select-option ${client.id === clientId ? 'selected' : ''}`}
                                  onClick={() => handleClientSelect(client)}
                                >
                                  {client.name}
                                  {client.id === clientId && <i className="fas fa-check"></i>}
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Subdomain Input */}
                  <div className="form-group">
                    <label htmlFor="subdomain">Subdomain</label>
                    <div className="subdomain-input-group">
                      <span className="subdomain-prefix">demo.</span>
                      <input
                        type="text"
                        id="subdomain"
                        className="subdomain-input"
                        value={subdomain}
                        onChange={(e) => setSubdomain(e.target.value)}
                        placeholder="Enter subdomain"
                        required
                      />
                      <span className="subdomain-suffix">.assetshield.trailio.io</span>
                    </div>
                  </div>

                  {/* Superadmin Email Input */}
                  <div className="form-group">
                    <label htmlFor="superadminEmail">Superadmin Email</label>
                    <div className="input-group">
                      <i className="fas fa-envelope input-icon"></i>
                      <input
                        type="email"
                        id="superadminEmail"
                        value={superadminEmail}
                        onChange={(e) => setSuperadminEmail(e.target.value)}
                        placeholder="Enter superadmin email"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-actions">
                    <button 
                      type="button" 
                      className="btn btn-secondary" 
                      onClick={closeModal}
                      disabled={submitting}
                    >
                      <i className="fas fa-times"></i> Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="btn btn-primary"
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i> Creating...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-check"></i> Create Demo Account
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ApplicationsPage; 